import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

/**
 * StatCard — standardized metric tile.
 *
 * Layout (top → bottom, padding 20px, radius 12, white bg, --shadow-card):
 *   eyebrow label + trend chip
 *   hero metric (36/700, -0.02em) + unit (14/500, muted)
 *   sub-line (13, muted)
 *   optional progress bar (4px, semantic)
 *
 * Trend semantics: caller passes `trend` (signed number) and `trendIsGood`
 *   (true = up = green, false = up = red). Defaults follow direction-of-good.
 *   For metrics where down-is-good (Energy Use, CO2), pass `goodWhenDown`.
 *
 * Props:
 *   label         string       — eyebrow (uppercased visually)
 *   value         string       — hero metric, e.g. "482"
 *   unit          string?      — unit appended next to value
 *   trend         number?      — signed delta %, optional
 *   trendLabel    string?      — override trend text
 *   goodWhenDown  bool         — invert semantics (down trend = good)
 *   sub           string?      — sub-line text
 *   progress      number?      — 0..1 progress fill
 *   intent        string?      — 'success' | 'warning' | 'danger' | 'info' | 'neutral'
 */
const TREND_CHIP = {
  good:    { bg: '#e3f0d8', fg: '#2f5a2c' },
  bad:     { bg: '#fadcdc', fg: '#a4282f' },
  neutral: { bg: '#ececea', fg: '#5b5f5b' },
};

const PROGRESS_COLOR = {
  success: '#3D6B38',
  warning: '#C9903A',
  danger:  '#B85052',
  info:    '#2c4f7a',
  neutral: '#7A8B77',
};

export const StatCard = ({
  label,
  value,
  unit,
  trend,
  trendLabel,
  goodWhenDown = false,
  sub,
  progress,
  intent = 'success',
  className = '',
  ariaLabel,
}) => {
  const hasTrend = trend !== undefined && trend !== null;
  let chipKey = 'neutral';
  let TrendIcon = Minus;
  if (hasTrend) {
    if (trend > 0) {
      chipKey = goodWhenDown ? 'bad' : 'good';
      TrendIcon = TrendingUp;
    } else if (trend < 0) {
      chipKey = goodWhenDown ? 'good' : 'bad';
      TrendIcon = TrendingDown;
    }
  }
  const chipColors = TREND_CHIP[chipKey];
  const progressBg = PROGRESS_COLOR[intent] || PROGRESS_COLOR.success;
  const trendDisplay = trendLabel || (hasTrend ? `${trend > 0 ? '+' : ''}${trend}%` : null);
  const clampedPct = progress != null ? Math.min(100, Math.max(0, Math.round(progress * 100))) : null;

  return (
    <article
      className={[
        'flex flex-col bg-white rounded-[12px] border border-[rgba(20,20,20,0.06)] shadow-lb-card',
        'p-5 cursor-default select-none',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      aria-label={ariaLabel || `${label}: ${value}${unit ? ` ${unit}` : ''}`}
    >
      {/* Row 1: eyebrow + trend chip */}
      <div className="flex items-center justify-between gap-2">
        <p
          className="m-0 text-[12px] font-medium text-lb-muted uppercase tracking-[0.04em] leading-[1.4]"
        >
          {label}
        </p>
        {trendDisplay && (
          <span
            className="inline-flex items-center gap-1 rounded-[4px] px-1.5 py-[2px] text-[11px] font-medium leading-none"
            style={{ background: chipColors.bg, color: chipColors.fg }}
            aria-label={`Trend: ${trendDisplay}`}
          >
            <TrendIcon size={11} strokeWidth={2.2} aria-hidden="true" />
            <span>{trendDisplay}</span>
          </span>
        )}
      </div>

      {/* Row 2: hero metric + unit */}
      <div className="flex items-baseline gap-1.5 mt-2 mb-1">
        <span
          className="text-[36px] font-bold tracking-[-0.02em] text-lb-ink-strong leading-[1.15]"
        >
          {value}
        </span>
        {unit && (
          <span className="text-[14px] font-medium text-lb-muted leading-none">
            {unit}
          </span>
        )}
      </div>

      {/* Row 3: sub-line */}
      {sub && <p className="m-0 text-[13px] text-lb-muted leading-[1.5]">{sub}</p>}

      {/* Row 4: progress */}
      {clampedPct != null && (
        <div className="mt-auto pt-4">
          <div
            className="h-[4px] rounded-[2px] overflow-hidden"
            style={{ background: 'rgba(20,20,20,0.07)' }}
            role="progressbar"
            aria-valuenow={clampedPct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`${label} progress: ${clampedPct}%`}
          >
            <div
              className="h-full rounded-[2px] transition-all duration-700"
              style={{ width: `${clampedPct}%`, background: progressBg }}
            />
          </div>
        </div>
      )}
    </article>
  );
};
